import React, { useState, useEffect } from 'react';
import { CheckCircle, XCircle, Search, Download, ChevronLeft, ChevronRight, RefreshCcw, ArrowUpDown, ArrowUp, ArrowDown, MessageCircle } from 'lucide-react';
import * as XLSX from 'xlsx';
import { useNavigate } from 'react-router-dom';
import { perfilNoModulo } from '../../../utils/perfisDerivados';

const ProcessoSeletivoTriagem = () => {
    const [candidatos, setCandidatos] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isRefreshing, setIsRefreshing] = useState(false); // Controla o estado do botão de atualizar
    const [error, setError] = useState('');
    const navigate = useNavigate();
    
    // Estado para a Super Pop-up de Automação
    const [automacaoModal, setAutomacaoModal] = useState({
        isOpen: false,
        titulo: '',
        mensagem: '',
        onConfirm: null, // Função se clicar em SIM
        onCancel: null   // Função se clicar em NÃO
    });
    
    // Filtros
    const [filterNome, setFilterNome] = useState('');
    const [filterCpf, setFilterCpf] = useState('');
    const [filterVaga, setFilterVaga] = useState('');

    // --- ESTADOS DE PAGINAÇÃO ---
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 50; 

    // PASSO 1: Criamos um estado para guardar qual coluna estamos ordenando e em qual direção
    // 'chave' guarda o nome da coluna e 'direcao' pode ser 'asc' (A-Z) ou 'desc' (Z-A)
    const [ordenacao, setOrdenacao] = useState({ chave: null, direcao: 'asc' });

    const userStr = localStorage.getItem('user');
    const user = userStr ? JSON.parse(userStr) : {};
    const role = perfilNoModulo(user.perfil, 'processoseletivo');
    const canAction = role === 'Admin' || role === 'Gerente' || role === 'RH' || role === 'ProcessoSeletivo' || role === 'Diretoria';

    // Gera a lista suspensa dinâmica das vagas
    const vagasDisponiveis = [...new Set(candidatos.map(c => c.VAGA_SELECIONADA).filter(Boolean))].sort();

    // --- FUNÇÃO DE CARREGAMENTO SEPARADA ---
    const carregarDados = async (showRefreshAnim = false) => {
        if (showRefreshAnim) setIsRefreshing(true);
        
        try {
            const token = localStorage.getItem('authToken');
            const headers = { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` };

            const response = await fetch('/api/processo-seletivo/candidatos', { headers });

            if (!response.ok) throw new Error("Erro ao carregar os dados.");

            setCandidatos(await response.json());
            setError(''); // Limpa qualquer erro anterior
        } catch (err) {
            setError(err.message);
        } finally {
            setIsLoading(false);
            if (showRefreshAnim) setIsRefreshing(false);
        }
    };

    useEffect(() => {
        carregarDados();
    }, []);

    // Volta para a página 1 sempre que um filtro for alterado
    useEffect(() => {
        setCurrentPage(1);
    }, [filterNome, filterCpf, filterVaga, ordenacao]);

    // --- FILTRO: Exibe APENAS quem tem Status 1 E Origem na tabela de Inscrição ---
    const candidatosFiltrados = candidatos.filter((cand) => {
        if (cand.STATUS_ETAPA !== 1 || cand.ORIGEM !== 'Inscricao') return false;
        
        const matchNome = cand.NOME_COMPLETO?.toLowerCase().includes(filterNome.toLowerCase());
        const matchCpf = cand.CPF?.replace(/[^\d]/g, '').includes(filterCpf.replace(/[^\d]/g, ''));
        const matchVaga = cand.VAGA_SELECIONADA?.toLowerCase().includes(filterVaga.toLowerCase());
        return matchNome && matchCpf && matchVaga;
    });

    // --- LÓGICA DE PAGINAÇÃO ---
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = candidatosFiltrados.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(candidatosFiltrados.length / itemsPerPage);

    const exportarExcel = () => {
        const dataToExport = candidatosFiltrados.map(cand => {
        // Monta a string da observação. VINCULO_FOLHA vem do backend: 'ATUAL' | 'EX' | null.
        let obsText = [];
        if (cand.VINCULO_FOLHA === 'ATUAL') obsText.push('Funcionário atual');
        if (cand.VINCULO_FOLHA === 'EX') obsText.push('Ex-funcionário');
        if (cand.QTD_PARTICIPACOES > 1) obsText.push(`Participou ${cand.QTD_PARTICIPACOES}x`);

        return {
            'Data da Inscrição': new Date(cand.DATA_INSCRICAO).toLocaleDateString('pt-BR'),
            'Nome Completo': cand.NOME_COMPLETO,
            'CPF': cand.CPF.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4"),
            'Celular': cand.CELULAR,
            'Regiões Disponíveis': cand.REGIOES_DISPONIVEIS || 'Não informada',
            'Vaga Selecionada': cand.VAGA_SELECIONADA || 'Não informada',
            'Observação': obsText.length > 0 ? obsText.join(' / ') : 'Novo',
            'Status': 'Etapa 1'
        };
    });
        const worksheet = XLSX.utils.json_to_sheet(dataToExport);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Inscritos_Etapa1");
        XLSX.writeFile(workbook, "Controle_Inscritos_Etapa1.xlsx");
    };

    const handleAprovarTriagem = async (idCandidato, origem) => {
        const confirmarAprovacao = window.confirm("Aprovar currículo e mover para Agendamento de Entrevista?");
        if (!confirmarAprovacao) return;

        // Criamos uma função interna que fará o disparo real para o Backend
        const executarAprovacao = async (enviarMensagem) => {
            // Fecha o modal gigante
            setAutomacaoModal({ ...automacaoModal, isOpen: false });

            try {
                const token = localStorage.getItem('authToken');
                const response = await fetch('/api/processo-seletivo/aprovar-etapa2', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                    // Dispara true ou false dependendo do botão que foi clicado no modal
                    body: JSON.stringify({ idCandidato, origem, enviar_automacao: enviarMensagem }) 
                });
                
                const data = await response.json();
                if (!response.ok) {
                    alert("Erro: " + data.message); 
                    return;
                }
                
                setCandidatos(prevCandidatos => prevCandidatos.filter(cand => cand.ID !== idCandidato));
            } catch (error) { 
                alert("Erro de conexão ao aprovar candidato."); 
            }
        };

        // ABRE O MODAL GIGANTE
        setAutomacaoModal({
            isOpen: true,
            titulo: 'Disparar Automação? 🤖',
            mensagem: 'Deseja enviar a mensagem automática de WhatsApp avisando o candidato sobre a aprovação?',
            onConfirm: () => executarAprovacao(true), // Se clicar no Verde
            onCancel: () => executarAprovacao(false)  // Se clicar no Cinza
        });
    };

    const handleReprovarTriagem = async (idCandidato, origem) => {
        const executarReprovacao = async (enviarMensagem) => {
            setAutomacaoModal({ ...automacaoModal, isOpen: false });

            try {
                const token = localStorage.getItem('authToken');
                const response = await fetch('/api/processo-seletivo/reprovar', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                    body: JSON.stringify({ 
                        idCandidato, 
                        motivo: "Histórico", 
                        origem, 
                        enviar_automacao: enviarMensagem 
                    }) 
                });
                
                if (!response.ok) {
                    const data = await response.json();
                    return alert("Erro ao reprovar: " + data.message);
                }
                
                setCandidatos(prevCandidatos => prevCandidatos.filter(cand => cand.ID !== idCandidato));
            } catch (error) { 
                alert("Erro de conexão ao reprovar candidato."); 
            }
        };

        setAutomacaoModal({
            isOpen: true,
            titulo: 'Reprovar por Histórico? 🛑',
            mensagem: 'Deseja arquivar este currículo e enviar a mensagem "Cadastro confirmado! ✅ Aguarde retorno" via WhatsApp?',
            onConfirm: () => executarReprovacao(true), 
            onCancel: () => executarReprovacao(false)  
        });
    };

    // Função que é chamada ao clicar no cabeçalho
        const lidarComOrdenacao = (chave) => {
            let direcao = 'asc';
            if (ordenacao.chave === chave && ordenacao.direcao === 'asc') {
                direcao = 'desc';
            }
            setOrdenacao({ chave, direcao });
        };
    
        // Função para renderizar o ícone correto no cabeçalho
        const renderizarIconeOrdenacao = (chave) => {
            if (ordenacao.chave !== chave) return <ArrowUpDown size={14} color="#cbd5e1" />;
            if (ordenacao.direcao === 'asc') return <ArrowUp size={14} color="#0f172a" />;
            return <ArrowDown size={14} color="#0f172a" />;
        };

    if (isLoading) return <div style={{ padding: '40px', textAlign: 'center', color: '#64748b', fontWeight: '500' }}>Carregando dados do servidor...</div>;
    if (error) return <div style={{ padding: '24px', textAlign: 'center', color: '#e11d48', backgroundColor: '#fff1f2', borderRadius: '16px' }}>{error}</div>;

    return (
        <div style={{ animation: 'fadeIn 0.3s ease-in-out' }}>
            <div style={{ marginBottom: '24px' }}>
                <h2 style={{ fontSize: '24px', fontWeight: '900', color: '#0f172a', marginBottom: '8px', margin: 0 }}>Inscritos no Processo (Triagem)</h2>
                <p style={{ color: '#64748b', fontSize: '15px', margin: 0 }}>Triagem inicial de currículos. Aprove para mover para o Agendamento de Entrevistas.</p>
            </div>

            {/* BARRA DE FILTROS E EXPORTAÇÃO */}
            <div style={{ backgroundColor: '#fff', borderRadius: '12px', padding: '20px', border: '1px solid #e2e8f0', marginBottom: '32px', display: 'flex', flexWrap: 'wrap', gap: '16px', alignItems: 'flex-end', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                <div style={{ flex: '1 1 200px' }}>
                    <label style={{ display: 'block', fontSize: '12px', fontWeight: '800', color: '#475569', textTransform: 'uppercase', marginBottom: '8px' }}>Nome Completo</label>
                    <div style={{ position: 'relative' }}>
                        <Search size={16} color="#94a3b8" style={{ position: 'absolute', left: '12px', top: '12px' }} />
                        <input type="text" placeholder="Buscar por nome..." value={filterNome} onChange={(e) => setFilterNome(e.target.value)} style={{ width: '100%', padding: '10px 10px 10px 36px', borderRadius: '8px', border: '1px solid #cbd5e1', outline: 'none' }} />
                    </div>
                </div>
                <div style={{ flex: '1 1 200px' }}>
                    <label style={{ display: 'block', fontSize: '12px', fontWeight: '800', color: '#475569', textTransform: 'uppercase', marginBottom: '8px' }}>CPF</label>
                    <div style={{ position: 'relative' }}>
                        <Search size={16} color="#94a3b8" style={{ position: 'absolute', left: '12px', top: '12px' }} />
                        <input type="text" placeholder="Apenas números..." value={filterCpf} onChange={(e) => setFilterCpf(e.target.value.replace(/\D/g, ''))} style={{ width: '100%', padding: '10px 10px 10px 36px', borderRadius: '8px', border: '1px solid #cbd5e1', outline: 'none' }} />
                    </div>
                </div>
                <div style={{ flex: '1 1 200px' }}>
                    <label style={{ display: 'block', fontSize: '12px', fontWeight: '800', color: '#475569', textTransform: 'uppercase', marginBottom: '8px' }}>Vaga Selecionada</label>
                    <select 
                        value={filterVaga} 
                        onChange={(e) => setFilterVaga(e.target.value)} 
                        style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #cbd5e1', outline: 'none', color: '#475569', cursor: 'pointer', backgroundColor: '#fff' }}
                    >
                        <option value="">Todas as Vagas</option>
                        {vagasDisponiveis.map(vaga => <option key={vaga} value={vaga}>{vaga}</option>)}
                    </select>
                </div>
                <div style={{ flex: '0 0 auto', display: 'flex', gap: '12px' }}>
                    
                    {/* NOVO BOTÃO DE ATUALIZAR */}
                    <button 
                        onClick={() => carregarDados(true)} 
                        disabled={isRefreshing}
                        style={{ backgroundColor: '#0f172a', color: '#fff', border: 'none', padding: '10px 20px', borderRadius: '8px', fontWeight: '800', cursor: isRefreshing ? 'wait' : 'pointer', display: 'flex', alignItems: 'center', gap: '8px', height: '40px', transition: 'all 0.2s', opacity: isRefreshing ? 0.7 : 1 }}
                    >
                        <RefreshCcw size={18} /> {isRefreshing ? 'Atualizando...' : 'Atualizar'}
                    </button>

                    <button onClick={exportarExcel} style={{ backgroundColor: '#10b981', color: '#fff', border: 'none', padding: '10px 20px', borderRadius: '8px', fontWeight: '800', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px', height: '40px' }}>
                        <Download size={18} /> Exportar
                    </button>
                </div>
            </div>

            <div style={{ backgroundColor: '#fff', borderRadius: '16px', border: '1px solid #e2e8f0', overflow: 'hidden' }}>
                <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
                        <thead>
                            <tr style={{ backgroundColor: '#f8fafc', borderBottom: '2px solid #e11d48' }}>
                                <th style={{ padding: '16px 24px', color: '#475569', fontSize: '13px', fontWeight: '800', whiteSpace: 'nowrap' }}>Data da Inscrição</th>
                                <th style={{ padding: '16px 24px', color: '#475569', fontSize: '13px', fontWeight: '800', whiteSpace: 'nowrap' }}>Candidato / Contato</th>
                                <th style={{ padding: '16px 24px', color: '#475569', fontSize: '13px', fontWeight: '800', whiteSpace: 'nowrap' }}>CPF</th>
                                <th style={{ padding: '16px 24px', color: '#475569', fontSize: '13px', fontWeight: '800', whiteSpace: 'nowrap' }}>Regiões Disponíveis</th>
                                <th style={{ padding: '16px 24px', color: '#475569', fontSize: '13px', fontWeight: '800', whiteSpace: 'nowrap' }}>Vaga (Interesse)</th>
                                <th style={{ padding: '16px 24px', color: '#475569', fontSize: '13px', fontWeight: '800', whiteSpace: 'nowrap', textAlign: 'center' }}>Observação</th>
                                {canAction && <th style={{ padding: '16px 24px', color: '#475569', fontSize: '13px', fontWeight: '800', whiteSpace: 'nowrap', textAlign: 'center' }}>Ações</th>}
                            </tr>
                        </thead>
                        <tbody>
                            {currentItems.length > 0 ? (
                                currentItems.map((cand) => (
                                    <tr key={cand.ID} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                        <td style={{ padding: '16px 24px', color: '#475569', fontSize: '14px', fontWeight: '600', whiteSpace: 'nowrap' }}>{new Date(cand.DATA_INSCRICAO).toLocaleDateString('pt-BR')}</td>
                                        <td style={{ padding: '16px 24px', whiteSpace: 'nowrap' }}>
                                            <strong style={{ color: '#0f172a', fontSize: '14px' }}>{cand.NOME_COMPLETO}</strong><br/>
                                            <span style={{ color: '#64748b', fontSize: '13px' }}>{cand.CELULAR}</span>
                                        </td>
                                        <td style={{ padding: '16px 24px', color: '#475569', fontSize: '13px', fontWeight: '600' }}>
                                            {cand.CPF || '-'}
                                        </td>
                                        <td style={{ padding: '16px 24px', color: '#475569', fontSize: '13px', fontWeight: '600' }}>
                                            {cand.REGIOES_DISPONIVEIS || '-'}
                                        </td>
                                        <td style={{ padding: '16px 24px', color: '#1d4ed8', fontSize: '13px', fontWeight: '800', whiteSpace: 'nowrap' }}>{cand.VAGA_SELECIONADA || '-'}</td>
                                        <td style={{ padding: '16px 24px', textAlign: 'center' }}>
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', alignItems: 'center' }}>
                                                {cand.VINCULO_FOLHA === 'ATUAL' && (
                                                    <span title="CPF está na folha SEM data de quitação — trabalha aqui hoje" style={{ backgroundColor: '#eff6ff', color: '#1d4ed8', padding: '4px 10px', borderRadius: '6px', fontSize: '11px', fontWeight: '800', whiteSpace: 'nowrap' }}>
                                                        Funcionário atual
                                                    </span>
                                                )}
                                                {cand.VINCULO_FOLHA === 'EX' && (
                                                    <span title="CPF está na folha COM data de quitação — já trabalhou aqui e saiu" style={{ backgroundColor: '#f1f5f9', color: '#475569', padding: '4px 10px', borderRadius: '6px', fontSize: '11px', fontWeight: '800', whiteSpace: 'nowrap' }}>
                                                        Ex-funcionário
                                                    </span>
                                                )}

                                                {cand.QTD_PARTICIPACOES > 1 ? (
                                                    <span title={`Este CPF se inscreveu ${cand.QTD_PARTICIPACOES} vezes no total`} style={{ backgroundColor: '#e0e7ff', color: '#1e40af', padding: '4px 10px', borderRadius: '6px', fontSize: '11px', fontWeight: '800', whiteSpace: 'nowrap' }}>
                                                        Participou {cand.QTD_PARTICIPACOES}x
                                                    </span>
                                                ) : null}

                                                {!cand.VINCULO_FOLHA && !(cand.QTD_PARTICIPACOES > 1) && (
                                                    <span style={{ color: '#94a3b8' }}>-</span>
                                                )}
                                            </div>
                                        </td>
                                        {canAction && (
                                            <td style={{ padding: '16px 24px' }}>
                                                <div style={{ display: 'flex', gap: '8px', justifyContent: 'center', alignItems: 'center' }}>
                                                    <button onClick={() => handleAprovarTriagem(cand.ID, cand.ORIGEM)} title="Aprovar e mover para Agendamento" style={{ width: '36px', height: '36px', backgroundColor: '#fff', color: '#059669', border: '1px solid #a7f3d0', borderRadius: '8px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, transition: 'background-color 0.2s' }} onMouseOver={e => e.currentTarget.style.backgroundColor = '#ecfdf5'} onMouseOut={e => e.currentTarget.style.backgroundColor = '#fff'}><CheckCircle size={18} /></button>
                                                    <button onClick={() => handleReprovarTriagem(cand.ID, cand.ORIGEM)} title="Reprovar candidato" style={{ width: '36px', height: '36px', backgroundColor: '#fff', color: '#e11d48', border: '1px solid #fbb6c4', borderRadius: '8px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, transition: 'background-color 0.2s' }} onMouseOver={e => e.currentTarget.style.backgroundColor = '#fff1f2'} onMouseOut={e => e.currentTarget.style.backgroundColor = '#fff'}><XCircle size={18} /></button>
                                                </div>
                                            </td>
                                        )}
                                    </tr>
                                ))
                            ) : (
                                <tr><td colSpan={canAction ? 7 : 6} style={{ padding: '40px', textAlign: 'center', color: '#94a3b8' }}>Nenhum candidato aguardando triagem.</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
                
                {candidatosFiltrados.length > 0 && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 24px', backgroundColor: '#f8fafc', borderTop: '1px solid #e2e8f0' }}>
                        <span style={{ fontSize: '14px', color: '#64748b', fontWeight: '600' }}>
                            Mostrando <strong style={{color: '#0f172a'}}>{indexOfFirstItem + 1}</strong> a <strong style={{color: '#0f172a'}}>{Math.min(indexOfLastItem, candidatosFiltrados.length)}</strong> de <strong style={{color: '#0f172a'}}>{candidatosFiltrados.length}</strong> inscritos
                        </span>
                        
                        <div style={{ display: 'flex', gap: '8px' }}>
                            <button
                                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                                disabled={currentPage === 1}
                                style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 16px', borderRadius: '8px', border: '1px solid #cbd5e1', backgroundColor: currentPage === 1 ? '#f1f5f9' : '#fff', color: currentPage === 1 ? '#94a3b8' : '#0f172a', fontWeight: '800', cursor: currentPage === 1 ? 'not-allowed' : 'pointer', transition: 'all 0.2s', boxShadow: currentPage === 1 ? 'none' : '0 1px 2px rgba(0,0,0,0.05)' }}
                            >
                                <ChevronLeft size={16} /> Anterior
                            </button>
                            
                            <span style={{ display: 'flex', alignItems: 'center', padding: '0 12px', fontSize: '14px', fontWeight: '800', color: '#475569' }}>
                                Pág. {currentPage} de {totalPages}
                            </span>

                            <button
                                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                                disabled={currentPage === totalPages || totalPages === 0}
                                style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 16px', borderRadius: '8px', border: '1px solid #cbd5e1', backgroundColor: currentPage === totalPages || totalPages === 0 ? '#f1f5f9' : '#fff', color: currentPage === totalPages || totalPages === 0 ? '#94a3b8' : '#0f172a', fontWeight: '800', cursor: currentPage === totalPages || totalPages === 0 ? 'not-allowed' : 'pointer', transition: 'all 0.2s', boxShadow: (currentPage === totalPages || totalPages === 0) ? 'none' : '0 1px 2px rgba(0,0,0,0.05)' }}
                            >
                                Próxima <ChevronRight size={16} />
                            </button>
                        </div>
                    </div>
                )}
            </div>
            {/* ====== SUPER POP-UP DE AUTOMAÇÃO ====== */}
            {automacaoModal.isOpen && (
                <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(15, 23, 42, 0.85)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 99999, padding: '20px', animation: 'fadeIn 0.2s' }}>
                    <div style={{ backgroundColor: '#fff', borderRadius: '24px', width: '100%', maxWidth: '700px', padding: '48px 32px', textAlign: 'center', boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)' }}>
                        
                        <div style={{ backgroundColor: '#fef3c7', width: '96px', height: '96px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 24px auto' }}>
                            <MessageCircle size={48} color="#d97706" />
                        </div>
                        
                        <h2 style={{ fontSize: '32px', fontWeight: '900', color: '#0f172a', marginBottom: '16px' }}>
                            {automacaoModal.titulo}
                        </h2>
                        
                        <p style={{ fontSize: '18px', color: '#475569', marginBottom: '40px', lineHeight: '1.6' }}>
                            {automacaoModal.mensagem}
                        </p>
                        
                        <div style={{ display: 'flex', gap: '20px', justifyContent: 'center' }}>
                            <button 
                                onClick={() => automacaoModal.onCancel()} 
                                style={{ padding: '16px 24px', fontSize: '18px', borderRadius: '12px', border: '2px solid #e2e8f0', backgroundColor: '#f8fafc', color: '#64748b', fontWeight: '800', cursor: 'pointer', transition: 'all 0.2s', flex: 1 }}
                            >
                                Não, apenas salvar
                            </button>
                            
                            <button 
                                onClick={() => automacaoModal.onConfirm()} 
                                style={{ padding: '16px 24px', fontSize: '18px', borderRadius: '12px', border: 'none', background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)', color: '#fff', fontWeight: '900', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px', boxShadow: '0 10px 15px -3px rgba(16, 185, 129, 0.3)', flex: 1 }}
                            >
                                <CheckCircle size={24} /> Sim, Enviar Mensagem
                            </button>
                        </div>

                        <button 
                            onClick={() => setAutomacaoModal({ ...automacaoModal, isOpen: false })} 
                            style={{ marginTop: '32px', background: 'none', border: 'none', color: '#94a3b8', textDecoration: 'underline', cursor: 'pointer', fontSize: '15px', fontWeight: '600' }}
                        >
                            Cancelar tudo e fechar
                        </button>

                    </div>
                </div>
            )}
        </div>
    );
};

export default ProcessoSeletivoTriagem;